import { supabase } from '../supabase/client';

export interface QuestionPackage {
  id: string;
  organization: string;
  category: string;
  level_text: string;
  expireDate: string;
  isActive: boolean;
  packageSource?: string;
  closedBy?: string;
  closedAt?: string;
  closedReason?: string;
  deletedBy?: string;
  deletedAt?: string;
  durationType?: string;
}

export interface User {
  id: string;
  username: string;
  phone: string;
  password_hash?: string;
  isDisabled: boolean;
  isBanned: boolean;
  created_at: string;
  last_login_time: string;
  role: 'student' | 'teacher';
  isPaid: boolean;
  packageName: string;
  packageExpireDate: string;
  questionPackages: QuestionPackage[];
  recentStudyTime: string;
  studyCount: number;
  remark: string;
  audit_status: 'pending' | 'approved' | 'rejected';
  unit_type?: 'school' | 'institution' | 'individual';
  region?: string;
  unit_name?: string;
  reject_reason?: string;
}

class UserStore {
  private listeners: (() => void)[] = [];

  async getUsers(): Promise<User[]> {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('role', 'student')
      .order('created_at', { ascending: false });
    if (error) {
      return [];
    }
    const users = await Promise.all((data || []).map(async (profile) => {
      const packages = await this.getUserPackages(profile.id);
      return this.mapProfileToUser(profile, packages);
    }));
    return users;
  }

  async getUserById(id: string): Promise<User | null> {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error || !data) return null;
    const packages = await this.getUserPackages(id);
    return this.mapProfileToUser(data, packages);
  }

  private async getUserPackages(userId: string): Promise<QuestionPackage[]> {
    const { data, error } = await supabase
      .from('user_packages')
      .select('*')
      .eq('user_id', userId);
    if (error) return [];
    return (data || []).map(pkg => ({
      id: pkg.id,
      organization: pkg.organization,
      category: pkg.category,
      level_text: pkg.level_text,
      expireDate: pkg.expire_date,
      isActive: pkg.is_active,
      packageSource: pkg.package_source,
      closedBy: pkg.closed_by,
      closedAt: pkg.closed_at,
      closedReason: pkg.closed_reason,
      deletedBy: pkg.deleted_by,
      deletedAt: pkg.deleted_at,
      durationType: pkg.duration_type,
    }));
  }

  private async getUserExamStats(userId: string): Promise<{ recentStudyTime: string; studyCount: number }> {
    const { data: records, error } = await supabase
      .from('exam_records')
      .select('created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error || !records || records.length === 0) {
      return { recentStudyTime: '-', studyCount: 0 };
    }

    return {
      recentStudyTime: records[0].created_at,
      studyCount: records.length
    };
  }

  private async mapProfileToUser(profile: any, packages: QuestionPackage[]): Promise<User> {
    const today = new Date().toISOString().split('T')[0];
    const activePackages = packages.filter(p => p.isActive && p.expireDate >= today);
    const isPaid = activePackages.length > 0;
    const latestPackage = activePackages.sort((a, b) =>
      new Date(b.expireDate).getTime() - new Date(a.expireDate).getTime()
    )[0];

    const examStats = await this.getUserExamStats(profile.id);

    return {
      id: profile.id,
      username: profile.username,
      phone: profile.phone || '',
      isDisabled: profile.is_disabled || false,
      isBanned: profile.is_banned || false,
      created_at: profile.created_at,
      last_login_time: profile.last_login_time || profile.created_at,
      role: profile.role === 'teacher' ? 'teacher' : 'student',
      isPaid,
      packageName: isPaid ? 'VIP套餐' : '-',
      packageExpireDate: latestPackage?.expireDate || '-',
      questionPackages: packages,
      recentStudyTime: examStats.recentStudyTime,
      studyCount: examStats.studyCount,
      remark: profile.remark || '',
      audit_status: profile.audit_status || 'approved',
      unit_type: profile.unit_type,
      region: profile.region,
      unit_name: profile.unit_name,
      reject_reason: profile.reject_reason
    };
  }

  async addUser(user: Omit<User, 'id' | 'created_at' | 'last_login_time' | 'questionPackages' | 'recentStudyTime' | 'studyCount'>): Promise<User | null> {
    try {
      const password = user.password_hash || '123456';

      const { data: createData, error: createError } = await supabase.functions.invoke('user-management', {
        body: {
          action: 'create',
          username: user.username,
          mobile: user.phone,
          password_hash: password,
          role: user.role
        }
      });

      if (createError) {
        return null;
      }

      if (!createData?.success || !createData?.user?.id) {
        return null;
      }

      const userId = createData.user.id;

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .update({
          role: user.role,
          is_disabled: user.isDisabled,
          is_banned: user.isBanned,
          remark: user.remark,
          audit_status: user.audit_status || 'approved',
          unit_type: user.unit_type,
          region: user.region,
          unit_name: user.unit_name
        })
        .eq('id', userId)
        .select()
        .single();

      if (profileError) {
        return null;
      }

      const { error: accountError } = await supabase
        .from('user_accounts')
        .insert({
          mobile: user.phone,
          role: user.role,
          password_hash: password,
          status: 'approved',
          auth_user_id: userId,
          username: user.username
        });

      this.notify();
      return this.mapProfileToUser(profileData, []);
    } catch (error) {
      return null;
    }
  }

  async updateUser(id: string, updates: Partial<User>): Promise<boolean> {
    const dbUpdates: any = {};
    if (updates.username !== undefined) dbUpdates.username = updates.username;
    if (updates.phone !== undefined) dbUpdates.phone = updates.phone;
    if (updates.password_hash !== undefined) dbUpdates.password_hash = updates.password_hash;
    if (updates.isDisabled !== undefined) dbUpdates.is_disabled = updates.isDisabled;
    if (updates.isBanned !== undefined) dbUpdates.is_banned = updates.isBanned;
    if (updates.remark !== undefined) dbUpdates.remark = updates.remark;
    if (updates.audit_status !== undefined) dbUpdates.audit_status = updates.audit_status;
    if (updates.unit_type !== undefined) dbUpdates.unit_type = updates.unit_type;
    if (updates.region !== undefined) dbUpdates.region = updates.region;
    if (updates.unit_name !== undefined) dbUpdates.unit_name = updates.unit_name;
    if (updates.reject_reason !== undefined) dbUpdates.reject_reason = updates.reject_reason;

    const { error } = await supabase
      .from('profiles')
      .update(dbUpdates)
      .eq('id', id);

    if (error) return false;

    if (updates.username || updates.phone || updates.password_hash) {
      const accountUpdates: any = {};
      if (updates.username !== undefined) accountUpdates.username = updates.username;
      if (updates.phone !== undefined) accountUpdates.mobile = updates.phone;
      if (updates.password_hash !== undefined) accountUpdates.password_hash = updates.password_hash;

      await supabase
        .from('user_accounts')
        .update(accountUpdates)
        .eq('auth_user_id', id);
    }

    this.notify();
    return true;
  }

  async deleteUser(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { data, error } = await supabase.functions.invoke('teacher-service', {
        body: {
          action: 'delete_student',
          studentId: id
        }
      });

      if (error) {
        return { success: false, error: error.message };
      }

      if (!data?.success) {
        return { success: false, error: data?.message || '删除失败' };
      }

      this.notify();
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  async deleteUsers(ids: string[]): Promise<void> {
    for (const id of ids) {
      await this.deleteUser(id);
    }
  }

  async addQuestionPackage(
    userId: string,
    packageData: Omit<QuestionPackage, 'id'>,
    source: 'admin_grant' | 'self_purchase' = 'admin_grant',
    price?: number,
    actualPrice?: number,
    durationType?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('user_packages')
        .insert({
          user_id: userId,
          organization: packageData.organization,
          category: packageData.category,
          level_text: packageData.level_text,
          expire_date: packageData.expireDate,
          is_active: packageData.isActive,
          package_source: source,
          price: price || 0,
          actual_price: actualPrice || 0,
          duration_type: durationType || null
        })
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      this.notify();
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  async toggleQuestionPackage(userId: string, packageId: string, adminUsername: string): Promise<boolean> {
    try {
      const { data: pkg } = await supabase
        .from('user_packages')
        .select('*')
        .eq('id', packageId)
        .eq('user_id', userId)
        .single();

      if (!pkg) return false;

      const newIsActive = !pkg.is_active;

      const updateData: any = {
        is_active: newIsActive
      };

      if (!newIsActive) {
        updateData.closed_by = adminUsername;
        updateData.closed_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('user_packages')
        .update(updateData)
        .eq('id', packageId)
        .eq('user_id', userId);

      if (error) return false;

      this.notify();
      return true;
    } catch (error) {
      return false;
    }
  }

  subscribe(callback: () => void) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  private notify() {
    this.listeners.forEach(l => l());
  }
}

export const userStore = new UserStore();
